/**
 * One withdrawal as a ticket, opened from Withdrawal requests: its status and progress, what happens
 * next, the amounts, fee and destination, the transaction hash once paid, and
 * Cancel while it is pending or under review. Same look as the deposit ticket. When our team asks the
 * client to confirm the wallet, an "Action needed" box opens the Verify wallet sheet.
 */
import { useState, type ReactNode } from 'react';
import { CURRENCY_SYMBOL, fmt, fmtUsd, PAYOUT_RAIL, withdrawalAssetOf, type PayoutCurrency, type Withdrawal } from '@platform/shared';
import { cx } from '../../../components/ui';
import { api } from '../../../lib/api';
import { AIcon, Chip, CopyButton, errMsg, Notice, PillButton, PillLink, when } from '../kit';
import { cryptoIcon } from '../deposit/coins';
import { Sheet } from '../deposit/Sheet';
import { TicketFrame } from '../deposit/TicketFrame';
import { VerifyWallet } from './VerifyWallet';

export const STATUS: Record<Withdrawal['status'], [string, 'warn' | 'up' | 'down' | 'muted' | 'accent']> = {
  pending: ['Pending', 'warn'],
  // Our team is checking it. A compliance hold reads the same (the API never sends 'compliance' to a client).
  under_review: ['Under review', 'warn'],
  compliance: ['Under review', 'warn'],
  // Approved by staff, payout under way.
  approved: ['Processing', 'accent'],
  paid: ['Paid', 'up'],
  rejected: ['Rejected', 'down'],
  cancelled: ['Cancelled', 'muted'],
};

export const money = (amount: string, ccy: string) => {
  const sym = CURRENCY_SYMBOL[ccy as PayoutCurrency];
  return sym ? `${sym}${fmt(amount, 2)}` : `${fmt(amount, 2)} ${ccy}`;
};

/** "0.00180000 BTC", "99.00 USDT": BTC and ETH to 8 decimals. */
export const coinMoney = (amount: string, coin: string) => `${fmt(amount, coin === 'USDT' ? 2 : 8)} ${coin}`;

/** What the client gets from a withdrawal: the amount sent once recorded, otherwise the (estimated, or for an amount entered in the coin exact) amount. */
export function receivedText(w: Withdrawal): string {
  if (w.method === 'crypto') {
    const coin = w.coin ?? 'USDT';
    if (w.amountSent) return coinMoney(w.amountSent, coin);
    return `${coin === 'USDT' || w.coinFixed ? '' : '≈ '}${coinMoney(w.receive, coin)}`;
  }
  return w.amountSent ? money(w.amountSent, w.currency) : w.currency !== 'USD' && w.receiveEst ? `≈ ${money(w.receiveEst, w.currency)}` : fmtUsd(w.receive);
}

/** Icon of a withdrawal: the coin (USDT with its network), or a bank / card disc. */
export function WithdrawalIcon({ w, size = 30 }: { w: Withdrawal; size?: number }) {
  if (w.method === 'crypto') return cryptoIcon(w.coin, w.network, size);
  return (
    <span className="flex shrink-0 items-center justify-center rounded-full bg-pill text-fg" style={{ width: size, height: size }}>
      <AIcon name={w.method === 'card' ? 'card' : 'bank'} size={Math.round(size * 0.53)} />
    </span>
  );
}

/** Block explorer page of a crypto payout. */
function explorer(network: string | null, hash: string): string | null {
  const h = hash.trim();
  if (network === 'TRC20') return `https://tronscan.org/#/transaction/${h.replace(/^0x/, '')}`;
  if (network === 'BTC') return `https://mempool.space/tx/${h.replace(/^0x/, '')}`;
  const evm = h.startsWith('0x') ? h : `0x${h}`;
  if (network === 'ERC20' || network === 'ETH') return `https://etherscan.io/tx/${evm}`;
  if (network === 'BEP20') return `https://bscscan.com/tx/${evm}`;
  return null;
}

const short = (s: string) => (s.length > 20 ? `${s.slice(0, 10)}…${s.slice(-8)}` : s);
const STEPS = ['Requested', 'Under review', 'Processing', 'Paid'];

export function WithdrawalTicket({ w, onClose, onCancelled }: { w: Withdrawal; onClose: () => void; onCancelled: () => void }) {
  const [verifying, setVerifying] = useState(false);
  const crypto = w.method === 'crypto';
  const coin = w.coin ?? 'USDT';
  const usdt = crypto && coin === 'USDT';
  const asset = crypto ? withdrawalAssetOf(coin, w.network) : undefined;
  const fiatEst = !crypto && w.currency !== 'USD';
  const [label, tone] = STATUS[w.status];
  const at = { pending: 0, under_review: 1, compliance: 1, approved: 2, paid: 4, rejected: 0, cancelled: 0 }[w.status];
  const inReview = w.status === 'under_review' || w.status === 'compliance';
  const askVerify = inReview && w.clientAction === 'verify_wallet';
  const stopped = w.status === 'rejected' || w.status === 'cancelled';
  const how = crypto
    ? usdt
      ? `USDT on ${asset ? `${asset.chain} (${asset.network})` : (w.network ?? 'crypto')}`
      : `${asset?.label ?? coin} on the ${w.network} network`
    : w.method === 'card'
      ? `Card refund in ${w.currency}`
      : `${PAYOUT_RAIL[w.currency as PayoutCurrency] ?? 'Wire transfer'} in ${w.currency}`;
  const got = receivedText(w);
  // USDT shows the amount in USDT (1:1); BTC and ETH show the USD taken from the balance.
  const amountText = usdt ? `${fmt(w.amount, 2)} USDT` : fmtUsd(w.amount);

  const msg: [string, ReactNode] =
    w.status === 'pending'
      ? ['bg-warn-bg', <>We review withdrawals within 24 hours. The amount has left your balance and comes back if you cancel or we reject the request. You can cancel while it is pending.</>]
      : askVerify
        ? ['bg-warn-bg', <div key="a" className="flex flex-col items-start gap-3">
            <span>
              <b className="text-warn">Action needed: confirm this wallet is yours.</b> It takes a few minutes.
            </span>
            <PillButton size="sm" onClick={() => setVerifying(true)}>
              Verify wallet
            </PillButton>
          </div>]
      : inReview
        ? ['bg-warn-bg', 'Our team is reviewing this withdrawal. Your money is on hold for it, and we will email you as soon as it moves on.']
      : w.status === 'approved'
        ? ['bg-track', crypto ? `Approved. Your ${coin} is being sent on the ${w.network} network.` : w.method === 'card' ? 'Approved. Your card refund is on its way.' : `Approved. Your ${PAYOUT_RAIL[w.currency as PayoutCurrency] ?? 'wire transfer'} is on its way.`]
        : w.status === 'paid'
          ? ['bg-up-bg', <b key="p">{got.replace('≈ ', '')} was sent to {crypto ? 'your address' : w.destination}.</b>]
          : w.status === 'rejected'
            ? ['bg-down-bg', <>
                <b>This withdrawal was rejected{w.rejectReason ? `: ${w.rejectReason}` : '.'}</b> The {fmtUsd(w.amount)} is back in your balance.
              </>]
            : ['bg-track', `You cancelled this withdrawal. The ${fmtUsd(w.amount)} is back in your balance.`];

  const rows: [string, ReactNode][] = [
    ['Request', w.number],
    ['Amount', amountText],
    ['Fee', crypto ? coinMoney(w.fee, coin) : fmtUsd(w.fee)],
    [w.status === 'paid' ? 'Received' : 'You receive', got],
  ];
  if (fiatEst && w.rateEst && !w.amountSent) rows.push(['Indicative rate', `1 ${w.currency} ≈ ${fmtUsd(w.rateEst)}`]);
  if (crypto && !usdt && w.rateEst) rows.push(['Rate when requested', `1 ${coin} ${w.coinFixed ? '=' : '≈'} ${fmtUsd(w.rateEst)}`]);
  if (crypto && !usdt) rows.push(['Entered in', w.coinFixed ? coin : 'USD']);
  if (crypto) {
    rows.push(['Coin', coin]);
    rows.push(['Network', asset ? `${asset.chain} (${w.network})` : (w.network ?? '—')]);
    rows.push(['Address', w.address ?? w.destination]);
  } else {
    rows.push(['Method', how]);
    rows.push(['To', w.destination]);
  }
  rows.push(['Requested', when(w.createdAt, true)]);

  const link = crypto && w.txHash ? explorer(w.network, w.txHash) : null;

  return (
    <Sheet title="Withdrawal ticket" onClose={onClose}>
      <TicketFrame
        head={
          <>
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-4">
              <WithdrawalIcon w={w} size={44} />
              <div className="min-w-0">
                <div className="num text-[28px] leading-tight font-semibold md:text-[30px]">−{amountText}</div>
                <div className="truncate text-[14px] text-muted">{how}</div>
              </div>
            </div>
            <Chip tone={tone}>{label}</Chip>
          </div>
          <ol className="m-0 grid list-none grid-cols-4 gap-1.5 p-0" aria-label="Progress">
            {STEPS.map((t, i) => {
              const state = i < at || (i === 0 && stopped) ? 'done' : i === at && !stopped ? 'now' : i === 1 && stopped ? 'stop' : 'todo';
              return (
                <li key={t} aria-current={state === 'now' ? 'step' : undefined} className={cx('flex flex-col gap-1.5 text-[12px]', state === 'todo' ? 'text-muted' : 'text-fg')}>
                  <span className={cx('h-1 rounded-full', { done: 'bg-up', now: 'bg-accent', stop: 'bg-down', todo: 'bg-pill' }[state])} />
                  {i === 1 && stopped ? label : t}
                </li>
              );
            })}
          </ol>
          </>
        }
      >
          <div className={cx('rounded-[14px] px-4 py-3 text-[14px] leading-relaxed md:text-[15px]', msg[0])}>{msg[1]}</div>

          <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-[14px]">
            {rows.map(([t, v]) => (
              <div key={t} className="contents">
                <dt className="text-muted">{t}</dt>
                <dd className="num m-0 text-right break-all">{v}</dd>
              </div>
            ))}
            {w.txHash && (
              <div className="contents">
                <dt className="text-muted">Transaction</dt>
                <dd className="num m-0 text-right break-all">
                  {link ? (
                    <a href={link} target="_blank" rel="noreferrer" className="text-accent-fg underline-offset-2 hover:underline">
                      {short(w.txHash)} ↗
                    </a>
                  ) : (
                    short(w.txHash)
                  )}
                </dd>
              </div>
            )}
          </dl>

          <div className="flex flex-wrap gap-2">
            {(w.status === 'pending' || w.status === 'under_review') && <CancelWithdrawal w={w} onDone={onCancelled} />}
            {w.txHash && (
              <CopyButton value={w.txHash} size="sm" kind="secondary">
                Copy transaction hash
              </CopyButton>
            )}
            <CopyButton value={w.number} size="sm" kind="secondary">
              Copy request number
            </CopyButton>
            <PillLink to={`/account/support?topic=funding&ref=${encodeURIComponent(w.number)}`} size="sm" kind="secondary">
              Get help with this withdrawal
            </PillLink>
          </div>
      </TicketFrame>
      {verifying && <VerifyWallet w={w} onClose={() => setVerifying(false)} />}
    </Sheet>
  );
}

/** "Cancel withdrawal" with a confirm step; the amount goes back to the balance. */
function CancelWithdrawal({ w, onDone }: { w: Withdrawal; onDone: () => void }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cancel = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/withdrawals/${w.id}/cancel`);
      setAsking(false);
      onDone();
    } catch (x) {
      setErr(errMsg(x));
      setAsking(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {err && (
        <div className="w-full">
          <Notice msg={{ ok: false, text: err }} onClose={() => setErr(null)} />
        </div>
      )}
      {asking ? (
        <div className="flex w-full flex-col gap-3 rounded-[16px] border border-line-strong p-4">
          <b className="text-[15px]">Cancel this withdrawal?</b>
          <span className="text-[14px] text-muted">The {fmtUsd(w.amount)} goes straight back to your balance. You can request a new withdrawal at any time.</span>
          <div className="flex flex-wrap gap-2">
            <PillButton size="sm" kind="destructive" busy={busy} onClick={() => void cancel()}>
              Cancel withdrawal
            </PillButton>
            <PillButton size="sm" kind="secondary" onClick={() => setAsking(false)}>
              Keep it
            </PillButton>
          </div>
        </div>
      ) : (
        <PillButton size="sm" kind="destructive" onClick={() => setAsking(true)}>
          <AIcon name="x" size={16} />
          Cancel withdrawal
        </PillButton>
      )}
    </>
  );
}
